import { PropTypes } from 'typed/imports';
import { Component, SyntheticEvent } from 'react';
import { ComponentEventHandler1, ConnectedProps, ReactEventHandler, connect } from 'typed/components/utils';
import { State as Store } from 'store';
import { Link as ReactRouterLink, match, WithRouterProps, withRouter } from 'react-router';
import { LocationDescriptor } from 'history';
import { history, RuntimeRoute, RouteAccessType } from 'routes';
import { deepEqual } from 'typed/utils';
import './Link.less';

interface StoreProps {
    store: Store;
}

interface OwnProps {
    to?: LocationDescriptor;
    onlyActiveOnIndex?: boolean;
    skipQueryPreservation?: boolean;
    disabled?: boolean;
    className?: string;
    children: unknown; // how else to access them in `componentWillReceiveProps`?
    onClick?: ComponentEventHandler1<SyntheticEvent<unknown>>;
}

interface Props extends StoreProps, OwnProps, WithRouterProps, ConnectedProps { }

interface State { route: RuntimeRoute | undefined; }

class Link extends Component<Props, State> {
    static readonly propTypes = {
        location: PropTypes.object.isRequired,
        to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        onClick: PropTypes.func,
        className: PropTypes.string,
        onlyActiveOnIndex: PropTypes.bool,
        style: PropTypes.object,
        disabled: PropTypes.bool
    };

    static readonly defaultProps = {
        onlyActiveOnIndex: true,
        skipQueryPreservation: false,
        disabled: false,
        className: ''
    };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (this.props === nextProps || !deepEqual(this.props.to, nextProps.to)) {
            // match is implemented to work asynchronously, but if all routes are synchronous, it works synchronously: https://github.com/ReactTraining/react-router/issues/1207#issuecomment-240172647
            // we have our own component to load chunk asynchronously which also provides some additional functionality, so we should always use it instead of react-router's getComponent
            // if some route can't be find synchronously, we assume it's not accessible
            let
                route: RuntimeRoute | undefined,
                location = nextProps.to;
            while (location) {
                match({ ...nextProps, location }, (_error, redirect, routeProps) => {
                    location = redirect;
                    route = routeProps && routeProps.routes && routeProps.routes.last();
                });
            }
            this.setState({ route });
        }
    }

    shouldComponentUpdate(prevProps: Props, prevState: State) {
        return false ||
            !deepEqual(prevProps, this.props, 'store', 'router', 'routes') || // shallow is not enough, parents usually construct new `to` and `to.query` on every render
            this._isAccessible(prevProps, prevState) !== this._isAccessible(this.props, this.state);
    }

    _isAccessible(props: Props = this.props, state: State = this.state): boolean {
        const access = !state.route ?
            RouteAccessType.HIDDEN :
            state.route.getAccess(props.params, props.store, props.dispatch);
        return access === RouteAccessType.READONLY || access === RouteAccessType.FULL;
    }

    _onClick: ReactEventHandler = (event) => {
        if ((this.props as any).onClick) {
            (this.props as any).onClick(this, event);
        }
    }

    render() {
        /*
            TODO remove this pretty dirty hack
            previously <button class="link"/> or <anyTag class="button link"/> was used to override button styles and make it appear as a link
            but <Link className="button"/> also producing <a class="link button"/> should override Link's appearance to look like button
            for now just remove Link's "link" class if it has "button" class - it should appear as button anyway
            but later probably it's better to use name other than "link" in first case
        */
        const className = (this.props.className!.includes('button') ? '' : 'link ') + this.props.className;

        if (this.props.disabled || !this.props.to || !this._isAccessible()) {
            return (
                <ReactRouterLink className={className + ' disabled'} style={(this.props as any).style} disabled to="">
                    {this.props.children}
                </ReactRouterLink>
            );
        }
        else {
            const
                to = history.createLocation(this.props.to),
                active =
                    this.props.router.isActive(
                        !this.props.skipQueryPreservation ?
                            {
                                pathname: to.pathname,
                                // kinda hacky, but till this point we distinguished 'pages' by paths only
                                // and query only added some minor parameters or page mode, but not enough to consider it a different page (same title, same breadcrumb, same look, same 'entity to show')
                                // so we can check activeness only by path
                                query: this.props.location.query
                            } :
                            to,
                        this.props.onlyActiveOnIndex
                    ) &&
                    (!this.props.skipQueryPreservation || deepEqual(this.props.location.query, to.query));

            // TODO distinguish Layout's/pages' own/other? query parameters and make Link preserve them while staying in scope of Layout/some page/whatever (route additional property?)
            // currently we store some global/page's parameters in query to make them accessible from outside of the application,
            //      preserve them when page is refreshed accidentally, keep them when opening another copy of a tab etc
            // but handling such parameters was pretty hard - every time we added a parameter we had to check all of existing Links and vice versa (nobody did it actually),
            // otherwise e. g. every click on any Link would exit fullscreen mode
            // it would be much easier if Link itself would preserve some parameters which make sense both on current page and target page, 
            // but remove them when user's leaving to another "scope" or parameter has to be removed on purpose (e. g. "exit fullscreen" link)
            // for now a hack - hardcoded list of Layout's parameters and a flag to skip preservation to remove parameters when needed

            if (!this.props.skipQueryPreservation) {
                ['askSatisfactionFeedback', 'polls', 'programs', 'decisions', 'view', 'react_perf', 'general_settings', 'workshopID', 'stepNumber', 'workflowType', 'sprintTriggerID'].forEach(paramName => {
                    if (this.props.location.query && this.props.location.query[paramName] && !to.query[paramName]) {
                        to.query[paramName] = this.props.location.query[paramName];
                    }
                });
            }

            return (
                <ReactRouterLink className={className + (active ? ' active' : '')} style={(this.props as any).style} to={to} onClick={this._onClick}>
                    {this.props.children}
                </ReactRouterLink>
            );
        }
    }
}

// TODO is it possible to make `Router` to get location from store rather than history?
// currenly any navigation action sets new props to 'withRouter'ed components,
// and also updates `routing` section of the store, which updates `connect`ed components' props
// so every Link always tries to update twice
// `shouldComponentUpdate` helps to strip unnecessary renders, but with the amount of Links we have on every page,
// `shouldComponentUpdate` itself takes quite a lot of time
// and also it's easier to have just one source of current location - the store
export default withRouter<OwnProps>(connect<StoreProps, OwnProps & WithRouterProps>(state => ({ store: state }))(Link));